import {
	Jumbo,
	Separator,
	Container,
	DateIntervalNavigation,
	ITimeMachine,
	createTimeMachineFromQuery,
	Callout,
	Paragraph,
	Button,
	Popover,
	Subheading,
} from '@treams/treams-ui';

import _ from 'lodash';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';
import { useState } from 'react';

import * as graphql from 'generated/graphql';

import { AnalyticsMetrics } from './AnalyticsMetrics';
import useWorkspaceConfiguration from 'packages/kakapo-ui-application/hooks/useWorkspaceConfiguration';
import { useTranslation } from 'react-i18next';
import GroupSelector from 'goals/group-selector/GroupSelector';
import { DownloadIcon } from '@treams/treams-icons';
import DashboardDownloadScores from 'packages/kakapo-ui-application/pages/dashboard/DashboardDownloadScores';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

const PageBackground = styled(Flex)`
	background-color: ${(props) => props.theme.colors.whiteBlue};
	flex-grow: 1;
	flex-direction: column;
	width: 100%;
`;

const Grid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	gap: 20px 12px;
`;

function AnalyticsDownloads(props: { filters: graphql.AnalyticsFiltersV2 }) {
	const { t } = useTranslation();

	const filters = {
		startDate: props.filters.startDate,
		endDate: props.filters.endDate,
		groupIDs: props.filters.groups?.map((group) => group.id),
	};

	const [visible, setVisible] = useState(false);

	return (
		<Popover.Target
			visible={visible}
			contents={
				<Box p={3}>
					<Subheading>{t('dashboard.sidebar.downloads.title')}</Subheading>
					<Paragraph mb={3}>
						<a target="_blank" href={t('dashboard.sidebar.downloads.info.link')} rel="noreferrer">
							{t('dashboard.sidebar.downloads.info.button')}
						</a>
					</Paragraph>
					<DashboardDownloadScores filters={filters} />
				</Box>
			}
			onDismiss={() => setVisible(false)}
		>
			<Button
				icon={<DownloadIcon />}
				title={t('dashboard.sidebar.downloads.title')}
				type="secondary"
				onClick={() => setVisible(true)}
			/>
		</Popover.Target>
	);
}

export default function Analytics() {
	const { t } = useTranslation();
	const [timeMachine, setTimeMachine] = useState<ITimeMachine>(createTimeMachineFromQuery());
	const configuration = useWorkspaceConfiguration();

	const [groups, setGroups] = useState<graphql.Group[]>([]);

	const hasTeamAnalytics = useFeature(graphql.FeatureName.AnalyticsTeams);

	return (
		<>
			<Jumbo
				title={t('analytics.overview.title')}
				footer={<Separator />}
				button={
					<AnalyticsDownloads
						filters={{
							startDate: timeMachine.current[0],
							endDate: timeMachine.current[1],
							groups: groups.map((group) => _.pick(group, ['id', 'type'])),
						}}
					/>
				}
			/>
			<PageBackground>
				<Container py={4}>
					<Callout
						title={t('analytics.overview.instructions.title')}
						description={t('analytics.overview.instructions.description')}
						color="blue"
						mb={3}
					/>

					<Flex alignItems="center" mb={3}>
						<Paragraph flexGrow={0} flexShrink={0} mr={2}>
							{t('dashboard.sidebar.filters.departments.title')}:
						</Paragraph>
						<Box mb="-8px">
							<GroupSelector
								canSelect={(group) =>
									hasTeamAnalytics ||
									(group.viewerCanViewAnalytics && group.type === graphql.GroupType.Department)
								}
								onChange={setGroups}
								initialGroups={[]}
								remember="analyticsGroupIDs"
							/>
						</Box>
						<Box flexGrow={1} />
						{configuration.loading && <DateIntervalNavigation.Skeleton />}
						{configuration.data && (
							<DateIntervalNavigation
								value={timeMachine}
								onChange={(newValue) => {
									setTimeMachine(newValue);
								}}
								workspaceStartDate={
									configuration.data.startDate
										? moment(configuration.data.startDate).toDate()
										: undefined
								}
							/>
						)}
					</Flex>

					<Grid>
						<AnalyticsMetrics timeMachine={timeMachine} groups={groups} />
					</Grid>
				</Container>
			</PageBackground>
		</>
	);
}
